import { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, type Me } from "./api";
import i18n, { normalizeLanguage } from "./i18n";
import SignIn from "./pages/SignIn";
import Home from "./pages/Home";
import BookPage from "./pages/BookPage";
import Archive from "./pages/Archive";
import Onboarding from "./pages/Onboarding";
import Account from "./pages/Account";
import Share from "./pages/Share";
import Legal from "./pages/Legal";
import NewBookChoice from "./pages/NewBookChoice";
import StoryCompose from "./pages/StoryCompose";
import Admin from "./pages/Admin";
import Landing from "./pages/Landing";

export default function App() {
  const { t } = useTranslation();
  const [me, setMe] = useState<Me | null | undefined>(undefined);

  useEffect(() => {
    void api.me().then(setMe).catch(() => setMe(null));
  }, []);

  // The signed-in user's saved language preference is authoritative across
  // devices: once /api/me resolves, switch to it (overriding the first-paint
  // localStorage/browser guess). Null means "not chosen" — keep the guess.
  useEffect(() => {
    if (!me?.uiLanguage) return;
    const next = normalizeLanguage(me.uiLanguage);
    if (next !== normalizeLanguage(i18n.language)) void i18n.changeLanguage(next);
  }, [me?.uiLanguage]);

  if (me === undefined) {
    return (
      <div className="tp-app">
        <div className="tp-loading">{t("common.loading")}</div>
      </div>
    );
  }

  const needsOnboarding = me ? (!me.authorName || me.authorBirthYear == null) : false;

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Landing me={me} />} />
        <Route path="/login" element={me ? <Navigate to="/app" replace /> : <SignIn />} />
        <Route path="/share/:shortId" element={<Share />} />
        <Route path="/legal" element={<Legal />} />
        <Route
          path="/app/*"
          element={
            me == null ? (
              <Navigate to="/login" replace />
            ) : needsOnboarding ? (
              <Onboarding me={me} onSaved={setMe} />
            ) : (
              <AppRoutes me={me} setMe={setMe} />
            )
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

function AppRoutes({ me, setMe }: { me: Me; setMe: (m: Me) => void }) {
  return (
    <Routes>
      <Route index element={<Home me={me} />} />
      <Route path="account" element={<Account me={me} onSaved={setMe} />} />
      <Route path="admin" element={<Admin me={me} />} />
      <Route path="new" element={<NewBookChoice />} />
      <Route path="new/story" element={<StoryCompose />} />
      <Route path="archive" element={<Archive />} />
      <Route path="books/:bookId" element={<BookPage />} />
      <Route path="*" element={<Navigate to="/app" replace />} />
    </Routes>
  );
}
